import { observable, action } from "mobx";
import axios from "axios";
import { Api } from "services/Api";
import stores from "./index";
import Toast from "helpers/toaster";
import { Intent } from "@blueprintjs/core";
import _ from "lodash";
import $ from "jquery";
import qs from "qs";
import moment from "moment";
import { currentTime } from "../helpers/time";
import { emptyJobStart } from "helpers/jobTimes";
import JobModel from "../models/job_model";
import { priorityQueryValues } from "helpers/priorityTag";
var FileDownload = require("js-file-download");

// A fresh blank job form, so default start/end times are "now" each time the
// form is cleared rather than when the page loaded. The job form replaces an
// untouched start (emptyJobStart) with its own default times.
const emptyJob = () => ({
  isLoading: false,
  isError: false,
  object: {
    client_id: null,
    title: null,
    start_date: emptyJobStart(),
    end_date: currentTime(),
    po_number: null,
    service_type_id: null,
    technician_ids: [],
    selectedServiceType: [],
    location: null,
    client: null,
    reason_for_call: "",
    is_po_provided: true,
    work_report_ids: [],
    priority: "normal",
  },
});

const EMPTY_NOTES = {
  isLoading: false,
  isError: false,
  object: {
    body: "",
  },
};

// A fresh blank time log, so its default times are "now" rather than page load.
const emptyTimelog = () => ({
  start_time: moment()._d,
  end_time: moment().add(1, "hours")._d,
  user_id: "",
  description: "",
  work_report_id: "",
  is_overtime: false,
  hours: 1,
  job_id: "",
  status: "draft",
});

class JobStore {
  @observable showFutureJobsLoader = false;
  // Bumped when jobs are created or edited from the drawer, so views other
  // than the calendar (the dispatch map) can refresh.
  @observable dispatchChangedAt = 0;
  @observable showUntilJobsLoader = false;
  @observable showMarkAsInvoicedLoader = false;
  @observable showChangeAssignmrntStatusLoader = false;
  @observable requestingRestoreJob = false;

  @observable jobs = {
    isLoading: false,
    isError: false,
    objects: [],
    meta: {},
  };

  @observable unassignedJobs = {
    isLoading: false,
    isError: false,
    objects: [],
    meta: {},
  };

  @observable incompleteJobs = {
    isLoading: false,
    isError: false,
    objects: [],
    meta: {},
  };

  @observable clientJobs = {
    isLoading: false,
    isError: false,
    objects: [],
    meta: {},
  };

  @observable job = {
    isLoading: false,
    isError: false,
    object: null,
  };
  // The job the user is viewing. Set as soon as its fetch starts.
  currentJobId = null;
  // Request generations: a response lands only if it is still the latest
  // request of its kind, so a slow response cannot overwrite a newer one.
  jobWriteToken = 0;
  jobContentsToken = 0;
  notesToken = 0;
  timesheetsToken = 0;

  @observable materialLineItems = {
    isLoading: false,
    isError: false,
    object: [],
  };

  @observable images = {
    isLoading: false,
    isError: false,
    objects: [],
  };
  @observable imageUploadVersion = 0;

  @observable jobAssociations = {
    isLoading: false,
    isError: false,
    objects: [],
  };

  @observable notes = {
    isLoading: false,
    isError: false,
    objects: [],
  };

  @observable deleteJob = {
    isLoading: false,
    isError: false,
    object: {
      work_reports: true,
      estimates: true,
      invoices: true,
      id: "",
      event_number: "",
    },
  };

  @observable timelog = {
    isLoading: false,
    isError: false,
    object: emptyTimelog(),
  };

  @observable timeLogs = {
    isLoading: false,
    isError: false,
    objects: [],
  };

  @observable jobWorkReports = [];
  @observable jobEstimates = [];
  @observable jobInvoices = [];

  @observable persistJob = emptyJob();
  @observable persistNote = EMPTY_NOTES;

  @observable isServiceAgreement = false;

  @action
  createTimeLog(timeLog) {
    this.timelog.isLoading = true;
    let promise = Api.post(`/timesheets/`, { time_log: timeLog });

    promise
      .then((response) => {
        this.timelog.isLoading = false;
        this.timelog.isError = false;
        this.timelog.object = emptyTimelog();
        this.refreshJob({ uuid: timeLog.job_id });
      })
      .catch((error) => {
        this.timelog.isLoading = false;
        this.timelog.isError = true;
      });

    return promise;
  }

  @action
  loadJobTimesheets(job) {
    const token = ++this.timesheetsToken;
    this.timeLogs.isLoading = true;
    let promise = Api.post("/timesheets/search", {
      search: {
        where: {
          job_number: job.attributes.event_number,
        },
        order: { created_at: "desc" },
        page: 1,
        per_page: 25,
      },
    });

    promise
      .then((response) => {
        if (token !== this.timesheetsToken) return;
        this.timeLogs.isLoading = false;
        this.timeLogs.isError = false;
        this.timeLogs.objects = response.data.data;
      })
      .catch((error) => {
        if (token !== this.timesheetsToken) return;
        this.timeLogs.isLoading = false;
        this.timeLogs.isError = false;
      });
  }

  @action
  loadJobContents(job) {
    const token = ++this.jobContentsToken;
    this.jobAssociations.isLoading = true;
    let promise = Api.post("/work_reports/search", {
      search: {
        where: {
          event_number: job.attributes.event_number,
        },
        order: { created_at: "desc" },
        page: 1,
        per_page: 60,
      },
    });

    promise
      .then((response) => {
        if (token !== this.jobContentsToken) return;
        this.jobAssociations.isLoading = false;
        this.jobAssociations.isError = false;
        this.jobAssociations.objects = response.data.data;
        this.jobWorkReports = _.filter(response.data.data, function (content) {
          return content.attributes.kind === "WorkReport";
        });
        this.jobEstimates = _.filter(response.data.data, function (content) {
          return content.attributes.kind === "Quote";
        });
        this.jobInvoices = _.filter(response.data.data, function (content) {
          return content.attributes.kind === "Invoice";
        });
      })
      .catch((error) => {
        if (token !== this.jobContentsToken) return;
        this.jobAssociations.isLoading = false;
        this.jobAssociations.isError = true;
      });
  }

  @action
  clearJob() {
    this.persistJob = emptyJob();
  }

  @action
  createJob() {
    this.persistJob.isLoading = true;
    let promise = Api.post(`/jobs/`, { job: this.persistJob.object });

    promise
      .then((response) => {
        this.persistJob.isLoading = false;
        this.persistJob.isError = false;
        stores.store.isOpen = false;
        const bookingId = this.persistJob.object.booking_id;
        const jobClientId = this.persistJob.object.client_id;
        this.clearJob();
        // A job made from a client's page: refresh its jobs and counts.
        const shownClient = stores.clientStore.client.object;
        if (jobClientId && shownClient && shownClient.id === jobClientId && stores.viewStore.currentView === "Pages.Clients.Show") {
          this.loadClientJobs();
          stores.clientStore.loadAnalytics({ uuid: jobClientId });
        }
        // A job made from a booking page: reload the booking so it shows the job.
        const booking = stores.bookingStore.booking.object;
        if (bookingId && booking && booking.attributes && booking.attributes.id === bookingId) {
          stores.bookingStore.loadBooking({ uuid: bookingId });
        }
        // Stay on the current screen; the calendar and map refresh themselves.
        $("#calendar").fullCalendar("refetchEvents");
        this.dispatchChangedAt = Date.now();
      })
      .catch((error) => {
        this.persistJob.isLoading = false;
        this.persistJob.isError = true;
        if (
          error.response &&
          error.response.data &&
          error.response.data.errors
        ) {
          console.error(error.response.data.errors.join(", "));
          Toast.show({
            icon: "warning-sign",
            message: error.response.data.errors.join(", "),
            intent: Intent.DANGER,
          });
        }
      });

    return promise;
  }

  @action
  createNotes(job, notes) {
    this.persistNote.isLoading = true;
    let params = {
      commentable_type: "Job",
      commentable_id: job.attributes.id,
      comment: {
        body: notes,
      },
    };
    let promise = Api.post(`/comments/`, params);
    promise
      .then((response) => {
        this.persistNote.isLoading = false;
        this.persistNote.isError = false;
        this.notes.objects = response.data.comments.data;
      })
      .catch((error) => {
        this.persistNote.isLoading = false;
        this.persistNote.isError = true;
      });

    return promise;
  }

  @action
  updateNotes(job, notes, commentObject) {
    this.persistNote.isLoading = true;
    let params = {
      commentable_type: "Job",
      commentable_id: job.attributes.id,
      comment: {
        body: notes,
        id: commentObject.id,
      },
    };
    let promise = Api.put(`/comments/${commentObject.id}`, params);
    promise
      .then((response) => {
        this.persistNote.isLoading = false;
        this.persistNote.isError = false;
        this.notes.objects = response.data.comments.data;
      })
      .catch((error) => {
        this.persistNote.isLoading = false;
        this.persistNote.isError = true;
      });

    return promise;
  }

  @action
  deleteNote(note, job) {
    let promise = Api.delete(`/comments/${note.attributes.id}`);
    promise
      .then((response) => {
        this.loadNotes(job);
      })
      .catch((error) => {});

    return promise;
  }

  @action
  updateJob(job) {
    this.job.isLoading = true;
    let promise = Api.put(`/jobs/${job.id}`, { job: job });

    return promise
      .then((response) => {
        this.job.isLoading = false;
      })
      .catch((error) => {
        this.job.isLoading = false;
      });
  }

  @action
  remindTechs(job) {
    this.job.isLoading = true;
    let promise = Api.post(`/jobs/${job.id}/remind_techs`, {});

    promise
      .then((response) => {
        this.job.isLoading = false;
      })
      .catch((error) => {
        this.job.isLoading = false;
      });
  }

  // Open the edit-job drawer for a job by id (e.g. from the dispatch board,
  // where calendar events only carry part of the job).
  // `reloadList` ('jobs' | 'assignments') refreshes that list after saving.
  @action
  openEditJobDrawer(uuid, { reloadList } = {}) {
    return Api.get(`/jobs/${uuid}`).then((response) => {
      const job = new JobModel(response.data.data);
      const attributes = job.attributes;
      this.persistJob = {
        object: {
          ...attributes,
          isServiceAgreement: false,
          openedFromDispatchBoard: true,
          reloadList,
          location: { attributes: attributes.location },
          client: { attributes: attributes.client },
          selectedServiceType: [
            {
              value: attributes.service_type_id,
              selected: true,
              label: attributes.service_type_name,
            },
          ],
        },
      };
      stores.store.isOpen = true;
    });
  }

  @action
  updateSidebarJob() {
    this.persistJob.isLoading = true;
    let promise = Api.put(`/jobs/${this.persistJob.object.id}/update_job`, {
      job: this.persistJob.object,
    });

    promise
      .then((response) => {
        this.persistJob.isLoading = false;
        this.persistJob.isError = false;
        $("#calendar").fullCalendar("refetchEvents");
        this.dispatchChangedAt = Date.now();
        stores.store.isOpen = false;
        if (this.persistJob.object.isServiceAgreement) {
          stores.serviceAgreementStore.loadServiceAgreement({
            uuid: this.persistJob.object.service_agreement_id,
          });
        } else if (this.persistJob.object.reloadList === 'jobs') {
          this.loadJobs();
        } else if (this.persistJob.object.reloadList === 'assignments') {
          this.loadAssignments();
        } else if (!this.persistJob.object.openedFromDispatchBoard) {
          // Opened from the job page: reload it. From the dispatch board the
          // calendar refetch above is enough, and we stay on the board.
          this.loadJob({ uuid: this.persistJob.object.id });
        }
        this.clearJob();
      })
      .catch((error) => {
        this.persistJob.isLoading = false;
        this.persistJob.isError = true;
        console.error(error);
      });

    return promise;
  }

  // Writes the job returned by `request` to this.job.object, unless the user
  // has moved to another job or a newer write for this job has started.
  // Resolves to { applied, response }. Api cancels a request when an
  // identical one starts (opening a job runs its route twice), so a
  // cancelled request was superseded, not failed.
  writeJob(uuid, request) {
    const token = uuid === this.currentJobId ? ++this.jobWriteToken : null;
    return request.then(
      (response) => {
        if (token !== this.jobWriteToken) return { applied: false, response };
        this.job.isLoading = false;
        this.job.isError = false;
        this.job.object = new JobModel(response.data.data);
        return { applied: true, response };
      },
      (error) => {
        if (axios.isCancel(error)) return { applied: false };
        throw error;
      },
    );
  }

  @action
  changeJobStatus(job, event) {
    this.showMarkAsInvoicedLoader = true;
    const request = Api.post(`/jobs/${job.attributes.id}/change_status`, {
      event: event,
    });
    const done = () => (this.showMarkAsInvoicedLoader = false);

    return this.writeJob(job.attributes.id, request).then(
      (result) => { done(); return result; },
      (error) => { done(); throw error; },
    );
  }

  @action
  changeAssignmentStatus(event, assignment) {
    this.showChangeAssignmrntStatusLoader = true;
    let promise = Api.post(`/assignments/${assignment.id}/change_status`, {
      event: event,
    });

    promise
      .then((response) => {
        this.showChangeAssignmrntStatusLoader = false;
      })
      .catch((error) => {
        this.showChangeAssignmrntStatusLoader = false;
      });

    return promise;
  }

  @action
  refreshJob({ uuid }) {
    this.showChangeAssignmrntStatusLoader = true;
    const done = () => (this.showChangeAssignmrntStatusLoader = false);

    return this.writeJob(uuid, Api.get(`/jobs/${uuid}`)).then(
      (result) => { done(); return result; },
      (error) => { done(); throw error; },
    );
  }

  @action
  loadClientJobs() {
    this.clientJobs.isLoading = true;
    let promise = Api.post("/jobs/search", {
      search: window.stores.viewStore.clientJobSearch,
    });

    promise
      .then((response) => {
        this.clientJobs.isLoading = false;
        this.clientJobs.isError = false;
        this.clientJobs.objects = response.data.data;
        this.clientJobs.meta = response.data.meta;
      })
      .catch((error) => {
        this.clientJobs.isLoading = false;
        this.clientJobs.isError = true;
      });
  }

  @action
  loadIncompleteJobs() {
    this.incompleteJobs.isLoading = true;

    let promise = Api.post("/jobs/search", {
      search: window.stores.viewStore.incompleteJobSearch,
    });

    promise
      .then((response) => {
        this.incompleteJobs.isLoading = false;
        this.incompleteJobs.isError = false;
        this.incompleteJobs.objects = response.data.data;
        this.incompleteJobs.meta = response.data.meta;
      })
      .catch((error) => {
        this.incompleteJobs.isLoading = false;
        this.incompleteJobs.isError = true;
      });

    return promise;
  }

  @action
  loadUnassignedJobs() {
    this.unassignedJobs.isLoading = true;
    let promise = Api.post("/assignments/search", {
      search: window.stores.viewStore.unassignedJobSearch,
    });

    promise
      .then((response) => {
        this.unassignedJobs.isLoading = false;
        this.unassignedJobs.isError = false;
        this.unassignedJobs.objects = response.data.data;
        this.unassignedJobs.objects.map((job) => {
          if (
            job.attributes &&
            job.attributes.service_type &&
            job.attributes.service_type.id
          ) {
            job.attributes.serviceType = job.attributes.service_type;
          } else {
            // hard coded
            job.attributes.service_type = {
              color: "f13b52",
              name: "PMA",
              id: "05fb4684-5705-40e9-b919-3a01bc71b147",
            };
            job.attributes.serviceType = {
              color: "f13b52",
              name: "PMA",
              id: "05fb4684-5705-40e9-b919-3a01bc71b147",
            };
          }
          return job;
        });
        this.unassignedJobs.meta = response.data.meta;
      })
      .catch((error) => {
        this.unassignedJobs.isLoading = false;
        this.unassignedJobs.isError = true;
      });

    return promise;
  }

  @action
  loadJob({ uuid }) {
    this.currentJobId = uuid;
    this.job.isLoading = true;
    const stillViewing = () => uuid === this.currentJobId;

    return this.writeJob(uuid, Api.get(`/jobs/${uuid}`))
      .then(({ response }) => {
        if (!response || !stillViewing()) return;
        // A newer write of this same job may have won the store; the page
        // still needs this load's bookkeeping.
        this.job.isLoading = false;
        const job = new JobModel(response.data.data);
        stores.viewStore.ShowJob({ uuid }, job.attributes.event_number);
        this.loadJobContents(job);
      })
      .catch((error) => {
        if (!stillViewing()) return;
        this.job.isLoading = false;
        this.job.isError = true;
        console.error(error);
      });
  }

  @action
  loadImages({ page = 1, per_page = 25, order = "desc" } = {}) {
    this.images.isLoading = true;
    let promise = Api.get(`/jobs/${this.job.object.id}/images`, {
      params: {
        page: page,
        per_page: per_page,
        order: order,
      },
    });

    promise
      .then((response) => {
        this.images.isLoading = false;
        this.images.isError = false;
        this.images.objects = response.data.data;
      })
      .catch((error) => {
        this.images.isLoading = false;
        this.images.isError = true;
        console.error(error);
      });

    return promise;
  }

  @action
  uploadAttachments(files) {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append("file[]", files[i]);
    }
    let promise = Api.post(
      `/jobs/${this.job.object.id}/upload_attachments`,
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    promise.then(() => {
      this.imageUploadVersion += 1;
    });
    return promise;
  }

  @action
  deleteJobImage(image_id) {
    this.images.isLoading = true;
    let promise = Api.delete(`/jobs/${this.job.object.id}/detached_image`, {
      params: { image_id: image_id },
    });

    promise
      .then((response) => {
        this.images.isLoading = false;
        this.images.isError = false;
      })
      .catch((error) => {
        this.images.isLoading = false;
        this.images.isError = true;
        console.error(error);
      });

    return promise;
  }

  @action
  deleteImage(work_report_id, image_id) {
    this.images.isLoading = true;
    let promise = Api.delete(`/work_reports/${work_report_id}/detached_image`, {
      params: { image_id: image_id },
    });

    promise
      .then((response) => {
        this.images.isLoading = false;
        this.images.isError = false;
      })
      .catch((error) => {
        this.images.isLoading = false;
        this.images.isError = true;
        console.error(error);
      });

    return promise;
  }

  @action
  loadWorkReportLineItems(uuid) {
    this.materialLineItems.isLoading = true;
    let promise = Api.get(`/jobs/${uuid}/work_report_line_items/`);

    promise
      .then((response) => {
        //this.job.isLoading = false;

        this.materialLineItems.isError = false;
        this.materialLineItems.object = response.data;
      })
      .catch((error) => {
        this.materialLineItems.isLoading = false;
        this.materialLineItems.isError = true;
        console.error(error);
      });

    return promise;
  }

  @action
  loadJobForBooking({ uuid }) {
    this.currentJobId = uuid;
    this.job.isLoading = true;

    return this.writeJob(uuid, Api.get(`/jobs/${uuid}`)).catch((error) => {
      if (uuid !== this.currentJobId) return { applied: false };
      this.job.isLoading = false;
      this.job.isError = true;
      console.error(error);
      throw error;
    });
  }

  @action
  loadNotes(job) {
    const token = ++this.notesToken;
    this.notes.isLoading = true;
    let promise = Api.get(
      `/comments?` +
        qs.stringify({
          commentable_id: job.attributes.id,
          commentable_type: "Job",
        }),
    );

    promise
      .then((response) => {
        if (token !== this.notesToken) return;
        this.notes.isLoading = false;
        this.notes.isError = false;
        this.notes.objects = response.data.comments.data;
      })
      .catch((error) => {
        if (token !== this.notesToken) return;
        this.notes.isLoading = false;
        this.notes.isError = true;
        console.error(error);
      });

    return promise;
  }

  @action
  loadJobs() {
    this.jobs.isLoading = true;
    const { only_deleted, where, per_page, page, order } =
      stores.viewStore.jobSearch;
    let promise = Api.post(`/jobs/search`, {
      search: {
        only_deleted: only_deleted,
        where: { ...where, priority: priorityQueryValues(where.priority) },
        order: order,
        per_page: per_page,
        page: page,
      },
    });

    promise
      .then((response) => {
        this.jobs.isLoading = false;
        this.jobs.isError = false;
        this.jobs.objects = response.data.data;
        this.jobs.meta = response.data.meta;
      })
      .catch((error) => {
        this.jobs.isLoading = false;
        this.jobs.isError = true;
      });

    return promise;
  }

  @action
  loadAssignments() {
    this.jobs.isLoading = true;
    const { where, per_page, page, order } = stores.viewStore.jobSearch;
    let promise = Api.post(`/assignments/search`, {
      search: {
        where: { ...where, priority: priorityQueryValues(where.priority) },
        order: order,
        per_page: per_page,
        page: page,
      },
    });

    promise
      .then((response) => {
        this.jobs.isLoading = false;
        this.jobs.isError = false;
        this.jobs.objects = response.data.data;
        this.jobs.meta = response.data.meta;
      })
      .catch((error) => {
        this.jobs.isLoading = false;
        this.jobs.isError = true;
      });

    return promise;
  }

  @action
  destroyJob() {
    this.deleteJob.isLoading = true;
    let promise = Api.post(`/jobs/${this.deleteJob.object.id}/destroy_job`, {
      job: this.deleteJob.object,
    });

    promise
      .then((response) => {
        this.deleteJob.isLoading = false;
        this.deleteJob.isError = false;
        if (this.deleteJob.object.isServiceAgreement) {
          stores.serviceAgreementStore.loadServiceAgreement({
            uuid: this.deleteJob.object.service_agreement_id,
          });
        } else {
          stores.viewStore.dashboard();
        }
      })
      .catch((error) => {
        this.deleteJob.isLoading = false;
        this.deleteJob.isError = true;
      });

    return promise;
  }

  @action
  generateFutureJobs(no_of_jobs, service_agreement_id) {
    this.showFutureJobsLoader = true;
    let promise = Api.post(`/jobs/generate_future_jobs`, {
      job: {
        no_of_jobs: no_of_jobs,
        service_agreement_id: service_agreement_id,
      },
    });

    promise
      .then((response) => {
        this.showFutureJobsLoader = false;
        stores.serviceAgreementStore.loadServiceAgreement({
          uuid: service_agreement_id,
        });
      })
      .catch((error) => {
        this.showFutureJobsLoader = false;
      });

    return promise;
  }

  @action
  generateUntilJobs(untilDate, service_agreement_id) {
    this.showUntilJobsLoader = true;
    let promise = Api.post(`/jobs/generate_untildate_jobs`, {
      job: {
        until_date: untilDate,
        service_agreement_id: service_agreement_id,
      },
    });

    promise
      .then((response) => {
        this.showUntilJobsLoader = false;
        stores.serviceAgreementStore.loadServiceAgreement({
          uuid: service_agreement_id,
        });
      })
      .catch((error) => {
        this.showUntilJobsLoader = false;
      });

    return promise;
  }

  @action
  download_excel(searchObj) {
    let promise = Api.post(
      "/jobs/excel_bulk_download",
      { search: searchObj },
      { responseType: "arraybuffer" },
    );

    promise
      .then((response) => {
        FileDownload(response.data, "Jobs.xls");
      })
      .catch((error) => {});
  }

  @action
  restoreJob(job) {
    this.requestingRestoreJob = true;
    let promise = Api.post(`/jobs/${job.attributes.id}/restore_job`, {
      job: job.attributes,
    });

    promise
      .then((response) => {
        this.requestingRestoreJob = false;
      })
      .catch((error) => {
        this.requestingRestoreJob = false;
      });

    return promise;
  }
}

export default JobStore;
